import { useEffect, useState } from 'react';
import {
  acknowledgeAsset,
  allocateAsset,
  completeAssetMaintenance,
  createAsset,
  createAssetCategory,
  createAssetIncident,
  createAssetMaintenance,
  disposeAsset,
  getAssetDashboardSummary,
  getAssetDetail,
  getAssetReport,
  inspectAndReturnAsset,
  listAssetCategories,
  listAssets,
  listDepartments,
  listEmployees,
  listMyAssets,
  transferAsset,
  type Asset,
  type AssetCategory,
  type AssetDashboardSummary,
  type AssetDetail,
  type Department,
  type EmployeeAsset,
  type EmployeeRecord,
} from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Icon } from '../components/Icon';
import { StatusBadge } from '../components/StatusBadge';
import { formatMoney } from '../utils/formatters';

export function Assets() {
  const { can, session } = useAuth();
  const isAdmin = can('assets.manage') || can('organization.manage') || can('asset.manage');

  const [tab, setTab] = useState<
    'dashboard' | 'inventory' | 'myassets' | 'reports'
  >(isAdmin ? 'dashboard' : 'myassets');

  // Main Data States
  const [dashboard, setDashboard] = useState<AssetDashboardSummary | null>(null);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [myAssets, setMyAssets] = useState<EmployeeAsset[]>([]);
  const [categories, setCategories] = useState<AssetCategory[]>([]);
  const [employees, setEmployees] = useState<EmployeeRecord[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  // Filters for Asset Register
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterWarrantyExpiring, setFilterWarrantyExpiring] = useState(false);

  // Modals & Drawers
  const [detailModal, setDetailModal] = useState<string | null>(null);
  const [selectedAssetDetail, setSelectedAssetDetail] = useState<AssetDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [createModal, setCreateModal] = useState(false);
  const [allocModal, setAllocModal] = useState<Asset | null>(null);
  const [ackModal, setAckModal] = useState<EmployeeAsset | null>(null);
  const [transferModal, setTransferModal] = useState<Asset | null>(null);
  const [returnModal, setReturnModal] = useState<{ allocId: string; assetCode?: string; defaultCondition?: string } | null>(null);
  const [incidentModal, setIncidentModal] = useState<Asset | null>(null);
  const [maintenanceModal, setMaintenanceModal] = useState<Asset | null>(null);
  const [completeMaintModal, setCompleteMaintModal] = useState<{ id: string; code: string; assetCode: string } | null>(null);
  const [disposeModal, setDisposeModal] = useState<Asset | null>(null);
  const [categoryModal, setCategoryModal] = useState(false);

  // Create Asset Form
  const [code, setCode] = useState('');
  const [assetName, setAssetName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [category, setCategory] = useState('Laptop');
  const [brand, setBrand] = useState('Apple');
  const [model, setModel] = useState('MacBook Pro M3');
  const [serial, setSerial] = useState('');
  const [imei, setImei] = useState('');
  const [macAddress, setMacAddress] = useState('');
  const [cost, setCost] = useState(150000);
  const [purchaseDate, setPurchaseDate] = useState(new Date().toISOString().split('T')[0]);
  const [warrantyMonths, setWarrantyMonths] = useState(36);
  const [location, setLocation] = useState('Headquarters');
  const [departmentId, setDepartmentId] = useState('');
  const [condition, setCondition] = useState('new');
  const [notes, setNotes] = useState('');
  const [accessoriesInput, setAccessoriesInput] = useState('Charger, Laptop Bag, USB-C Adapter');

  // Allocation Form
  const [selectedEmpId, setSelectedEmpId] = useState('');
  const [expectedReturnDate, setExpectedReturnDate] = useState('');
  const [allocType, setAllocType] = useState<'permanent' | 'temporary' | 'project_based'>('permanent');
  const [allocCondition, setAllocCondition] = useState('good');
  const [allocAccessories, setAllocAccessories] = useState('');

  // Acknowledgement Form
  const [ackTermsAccepted, setAckTermsAccepted] = useState(false);
  const [ackComments, setAckComments] = useState('');

  // Transfer Form
  const [transferType, setTransferType] = useState<'employee_to_employee' | 'department_to_department' | 'location_to_location'>('employee_to_employee');
  const [toEmpId, setToEmpId] = useState('');
  const [toDeptId, setToDeptId] = useState('');
  const [toLoc, setToLoc] = useState('');
  const [transferReason, setTransferReason] = useState('');
  const [transferCondition, setTransferCondition] = useState('good');

  // Physical Return Inspection Form
  const [inspectCondition, setInspectCondition] = useState('good');
  const [inspectResult, setInspectResult] = useState<'passed' | 'minor_wear' | 'repair_required' | 'total_loss'>('passed');
  const [inspectHasDamage, setInspectHasDamage] = useState(false);
  const [inspectDamageDesc, setInspectDamageDesc] = useState('');
  const [inspectAccessoriesReturned, setInspectAccessoriesReturned] = useState('');
  const [inspectMissingAccessories, setInspectMissingAccessories] = useState('');
  const [inspectorRemarks, setInspectorRemarks] = useState('');

  // Incident Form
  const [incidentType, setIncidentType] = useState<'damaged' | 'lost' | 'stolen' | 'missing_accessory'>('damaged');
  const [incidentDate, setIncidentDate] = useState(new Date().toISOString().split('T')[0]);
  const [incidentDesc, setIncidentDesc] = useState('');
  const [incidentEstCost, setIncidentEstCost] = useState(10000);
  const [incidentEmpRespPct, setIncidentEmpRespPct] = useState(50);

  // Maintenance Form
  const [maintServiceType, setMaintServiceType] = useState<'preventive' | 'repair' | 'upgrade' | 'warranty_claim'>('repair');
  const [maintIssue, setMaintIssue] = useState('');
  const [maintStartDate, setMaintStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [maintEstCost, setMaintEstCost] = useState(5000);
  const [maintIsWarranty, setMaintIsWarranty] = useState(false);

  // Complete Maintenance Form
  const [compMaintDate, setCompMaintDate] = useState(new Date().toISOString().split('T')[0]);
  const [compMaintDowntime, setCompMaintDowntime] = useState(2);
  const [compMaintActualCost, setCompMaintActualCost] = useState(4500);
  const [compMaintNotes, setCompMaintNotes] = useState('Resolved and tested fully functional.');

  // Dispose Form
  const [disposeDate, setDisposeDate] = useState(new Date().toISOString().split('T')[0]);
  const [disposeReason, setDisposeReason] = useState('End of useful lifecycle & hardware depreciation');
  const [disposeProceeds, setDisposeProceeds] = useState(5000);

  // Category Form
  const [catCode, setCatCode] = useState('');
  const [catName, setCatName] = useState('');
  const [catGroup, setCatGroup] = useState<'it_assets' | 'communication' | 'office' | 'travel_ops' | 'software_digital' | 'other'>('it_assets');
  const [catPrefix, setCatPrefix] = useState('AST-');
  const [catLife, setCatLife] = useState(36);
  const [catRate, setCatRate] = useState(33.33);

  // Reports
  const [selectedReportType, setSelectedReportType] = useState('asset_register');
  const [reportRows, setReportRows] = useState<any[]>([]);
  const [loadingReport, setLoadingReport] = useState(false);

  async function loadData() {
    try {
      setLoading(true);
      const promises: Promise<any>[] = [listMyAssets().catch(() => ({ data: [] }))];

      if (isAdmin) {
        promises.push(getAssetDashboardSummary().catch(() => ({ data: null })));
        promises.push(
          listAssets({
            search: search || undefined,
            category: filterCategory || undefined,
            status: filterStatus || undefined,
            departmentId: filterDept || undefined,
            warrantyExpiringSoon: filterWarrantyExpiring || undefined,
          }).catch(() => ({ data: [] })),
        );
        promises.push(listAssetCategories().catch(() => ({ data: [] })));
        promises.push(listEmployees().catch(() => ({ items: [] })));
        promises.push(listDepartments().catch(() => ({ items: [] })));
      }

      const [myRes, dashRes, allRes, catRes, empRes, deptRes] = await Promise.all(promises);
      setMyAssets(myRes.data || []);
      if (dashRes) setDashboard(dashRes.data);
      if (allRes) setAssets(allRes.data || []);
      if (catRes) setCategories(catRes.data || []);
      if (empRes) setEmployees(empRes.items || []);
      if (deptRes) setDepartments(deptRes.items || []);
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to load asset management data' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData();
  }, [filterCategory, filterStatus, filterDept, filterWarrantyExpiring]);

  async function handleOpenDetail(assetId: string) {
    try {
      setDetailModal(assetId);
      setLoadingDetail(true);
      const res = await getAssetDetail(assetId);
      setSelectedAssetDetail(res.data);
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to load asset details' });
    } finally {
      setLoadingDetail(false);
    }
  }

  async function handleCreateAsset(e: React.FormEvent) {
    e.preventDefault();
    try {
      const accList = accessoriesInput
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .map((name) => ({ name, condition: 'good' }));

      const wEnd = new Date(new Date(purchaseDate).getTime() + warrantyMonths * 30 * 86400000).toISOString().split('T')[0];

      await createAsset({
        assetCode: code.trim() || undefined,
        categoryId: categoryId || undefined,
        category,
        assetName: assetName || `${brand} ${model}`.trim(),
        brand,
        model,
        serialNumber: serial || null,
        imei: imei || null,
        macAddress: macAddress || null,
        purchaseDate,
        purchaseCost: Number(cost),
        warrantyStart: purchaseDate,
        warrantyEnd: wEnd,
        warrantyProvider: brand,
        location,
        departmentId: departmentId || null,
        condition,
        status: 'in_stock',
        notes: notes || null,
        accessories: accList,
      });

      setMsg({ type: 'success', text: `Asset registered successfully into enterprise inventory!` });
      setCreateModal(false);
      setCode('');
      setAssetName('');
      setSerial('');
      setImei('');
      setMacAddress('');
      setNotes('');
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to register asset' });
    }
  }

  async function handleAllocateSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!allocModal || !selectedEmpId) return;
    try {
      const accs = allocAccessories.split(',').map((s) => s.trim()).filter(Boolean);
      await allocateAsset({
        assetId: allocModal.id,
        employeeId: selectedEmpId,
        expectedReturnDate: expectedReturnDate || null,
        allocationType: allocType,
        issuedCondition: allocCondition,
        accessoriesIncluded: accs,
      });

      setMsg({ type: 'success', text: `Asset ${allocModal.asset_code} allocated. Handover acknowledgement sent to employee!` });
      setAllocModal(null);
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to allocate asset' });
    }
  }

  async function handleAcknowledgeSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!ackModal || !ackTermsAccepted) return;
    try {
      await acknowledgeAsset(ackModal.allocation_id, {
        termsVersion: 'v1.0',
        comments: ackComments || null,
        status: 'accepted',
      });
      setMsg({ type: 'success', text: `Asset ${ackModal.asset_code} digitally acknowledged and accepted!` });
      setAckModal(null);
      setAckTermsAccepted(false);
      setAckComments('');
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to submit digital acknowledgement' });
    }
  }

  async function handleTransferSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!transferModal) return;
    try {
      await transferAsset({
        assetId: transferModal.id,
        transferType,
        toEmployeeId: transferType === 'employee_to_employee' ? toEmpId : null,
        toDepartmentId: transferType === 'department_to_department' ? toDeptId : null,
        toLocation: transferType === 'location_to_location' ? toLoc : null,
        conditionAtTransfer: transferCondition,
        reason: transferReason,
      });
      setMsg({ type: 'success', text: `Asset ${transferModal.asset_code} transfer logged with immutable history record!` });
      setTransferModal(null);
      setTransferReason('');
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to complete asset transfer' });
    }
  }

  async function handleReturnInspectionSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!returnModal) return;
    try {
      const retAccs = inspectAccessoriesReturned.split(',').map((s) => s.trim()).filter(Boolean);
      const missAccs = inspectMissingAccessories.split(',').map((s) => s.trim()).filter(Boolean);

      await inspectAndReturnAsset(returnModal.allocId, {
        conditionOnReturn: inspectCondition,
        accessoriesReturned: retAccs,
        missingAccessories: missAccs,
        hasDamage: inspectHasDamage,
        damageDescription: inspectDamageDesc || null,
        inspectionResult: inspectResult,
        inspectorRemarks: inspectorRemarks || null,
      });

      setMsg({ type: 'success', text: `Asset physical inspection completed and returned to inventory!` });
      setReturnModal(null);
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to complete return inspection' });
    }
  }

  async function handleCreateIncidentSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!incidentModal) return;
    try {
      await createAssetIncident({
        assetId: incidentModal.id,
        employeeId: incidentModal.current_holder_id,
        incidentType,
        incidentDate,
        description: incidentDesc,
        estimatedCost: Number(incidentEstCost),
        employeeResponsibilityPct: Number(incidentEmpRespPct),
      });

      setMsg({ type: 'success', text: `Incident logged for asset ${incidentModal.asset_code}. Assessment queued for review.` });
      setIncidentModal(null);
      setIncidentDesc('');
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to log incident' });
    }
  }

  async function handleCreateMaintenanceSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!maintenanceModal) return;
    try {
      await createAssetMaintenance({
        assetId: maintenanceModal.id,
        serviceType: maintServiceType,
        issueDescription: maintIssue,
        startDate: maintStartDate,
        repairCost: Number(maintEstCost),
        isWarrantyCovered: maintIsWarranty,
      });

      setMsg({ type: 'success', text: `Maintenance request created for asset ${maintenanceModal.asset_code}!` });
      setMaintenanceModal(null);
      setMaintIssue('');
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to create maintenance record' });
    }
  }

  async function handleCompleteMaintenanceSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!completeMaintModal) return;
    try {
      await completeAssetMaintenance(completeMaintModal.id, {
        completionDate: compMaintDate,
        downtimeDays: Number(compMaintDowntime),
        repairCost: Number(compMaintActualCost),
        resolutionNotes: compMaintNotes,
      });

      setMsg({ type: 'success', text: `Maintenance service completed for ${completeMaintModal.assetCode}!` });
      setCompleteMaintModal(null);
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to complete maintenance' });
    }
  }

  async function handleDisposeSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!disposeModal) return;
    try {
      await disposeAsset(disposeModal.id, {
        disposalDate: disposeDate,
        reason: disposeReason,
        disposalProceeds: Number(disposeProceeds),
      });

      setMsg({ type: 'success', text: `Asset ${disposeModal.asset_code} marked as disposed/retired.` });
      setDisposeModal(null);
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to dispose asset' });
    }
  }

  async function handleCreateCategorySubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      await createAssetCategory({
        categoryCode: catCode,
        categoryName: catName,
        groupType: catGroup,
        prefix: catPrefix,
        usefulLifeMonths: Number(catLife),
        defaultDepreciationRate: Number(catRate),
      });
      setMsg({ type: 'success', text: `Category ${catName} created successfully!` });
      setCategoryModal(false);
      setCatCode('');
      setCatName('');
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to create category' });
    }
  }

  async function handleFetchReport(type: string) {
    try {
      setSelectedReportType(type);
      setLoadingReport(true);
      const res = await getAssetReport(type);
      setReportRows(res.data.rows || []);
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to generate report' });
    } finally {
      setLoadingReport(false);
    }
  }

  function handleExportCSV() {
    if (reportRows.length === 0) return;
    const headers = Object.keys(reportRows[0]);
    const csvLines = [headers.join(',')];
    for (const row of reportRows) {
      const values = headers.map((h) => {
        const val = row[h];
        if (val === null || val === undefined) return '""';
        return `"${String(val).replace(/"/g, '""')}"`;
      });
      csvLines.push(values.join(','));
    }
    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${selectedReportType}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div>
      {/* Header */}
      <div className="page-head">
        <div>
          <span className="eyebrow">
            <Icon name="laptop" size="xs" />
            <span>IT & Enterprise Facilities</span>
          </span>
          <h1>Asset Lifecycle Management</h1>
        </div>
        {isAdmin && (
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn-quiet" onClick={() => setCategoryModal(true)}>
              <Icon name="layers" size="xs" />
              <span>Categories</span>
            </button>
            <button className="btn-primary" onClick={() => setCreateModal(true)}>
              <Icon name="plus" size="xs" />
              <span>Register Asset</span>
            </button>
          </div>
        )}
      </div>

      {msg && (
        <div className={`banner ${msg.type}`} style={{ marginBottom: 16 }}>
          <span>{msg.text}</span>
          <button
            onClick={() => setMsg(null)}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <Icon name="close" size="xs" />
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: 16, overflowX: 'auto', flexWrap: 'nowrap' }}>
        {isAdmin && (
          <button className={`tab-btn ${tab === 'dashboard' ? 'active' : ''}`} onClick={() => setTab('dashboard')}>
            <Icon name="dashboard" size="xs" />
            <span>Dashboard</span>
          </button>
        )}
        {isAdmin && (
          <button className={`tab-btn ${tab === 'inventory' ? 'active' : ''}`} onClick={() => setTab('inventory')}>
            <Icon name="laptop" size="xs" />
            <span>Asset Register ({assets.length})</span>
          </button>
        )}
        <button className={`tab-btn ${tab === 'myassets' ? 'active' : ''}`} onClick={() => setTab('myassets')}>
          <Icon name="monitor" size="xs" />
          <span>My Company Assets ({myAssets.length})</span>
        </button>
        {isAdmin && (
          <button className={`tab-btn ${tab === 'reports' ? 'active' : ''}`} onClick={() => { setTab('reports'); void handleFetchReport('asset_register'); }}>
            <Icon name="fileSpreadsheet" size="xs" />
            <span>Reports & Exports</span>
          </button>
        )}
      </div>

      {loading ? (
        <div className="panel">
          <div className="empty">Loading enterprise asset records...</div>
        </div>
      ) : tab === 'dashboard' && dashboard ? (
        /* DASHBOARD TAB */
        <div>
          {/* Top KPI Cards */}
          <div className="stat-grid" style={{ marginBottom: 20 }}>
            <div className="stat-card">
              <span className="stat-label">Total Assets</span>
              <strong className="stat-value">{dashboard.kpis.totalAssets}</strong>
              <span className="stat-help">Acquisition Value: {formatMoney(dashboard.kpis.totalAcquisitionValue)}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">In Use / Assigned</span>
              <strong className="stat-value" style={{ color: 'var(--color-primary)' }}>
                {dashboard.kpis.assignedAssets}
              </strong>
              <span className="stat-help">{dashboard.kpis.availableAssets} currently in stock</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Under Maintenance</span>
              <strong className="stat-value" style={{ color: dashboard.kpis.underMaintenance > 0 ? '#eab308' : 'inherit' }}>
                {dashboard.kpis.underMaintenance}
              </strong>
              <span className="stat-help">{dashboard.kpis.maintenanceDueNext30Days} scheduled this month</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Warranty Alerts</span>
              <strong className="stat-value" style={{ color: dashboard.kpis.warrantyNearingExpiry > 0 ? '#f97316' : 'inherit' }}>
                {dashboard.kpis.warrantyNearingExpiry}
              </strong>
              <span className="stat-help">{dashboard.kpis.assetsUnderWarranty} total under active warranty</span>
            </div>
          </div>

          {/* Secondary Cards: Returns & Incidents */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginBottom: 20 }}>
            <div className="panel">
              <div className="panel-head">
                <strong>Lifecycle Health & Returns</strong>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <span>Available in Stock</span>
                  <strong className="badge success">{dashboard.kpis.availableAssets}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <span>Pending Return / Check-in</span>
                  <strong className="badge warning">{dashboard.kpis.returnPending}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <span>Damaged / Repair Required</span>
                  <strong className="badge danger">{dashboard.kpis.damagedAssets}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                  <span>Lost / Stolen</span>
                  <strong className="badge danger">{dashboard.kpis.lostAssets}</strong>
                </div>
              </div>
            </div>

            <div className="panel">
              <div className="panel-head">
                <strong>Categories & Distribution</strong>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 14 }}>
                {dashboard.categoryBreakdown.map((c) => (
                  <div key={c.categoryName} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 13 }}>{c.categoryName}</span>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span className="mono muted" style={{ fontSize: 12 }}>{formatMoney(c.totalValue)}</span>
                      <span className="badge" style={{ minWidth: 28, textAlign: 'center' }}>{c.count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : tab === 'inventory' ? (
        /* ASSET REGISTER TAB */
        <div>
          {/* Filter Bar */}
          <div className="panel" style={{ marginBottom: 16, padding: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label style={{ fontSize: 11 }}>Search Code / Serial / Spec</label>
                <input
                  type="text"
                  placeholder="e.g. AST-LAP-001 or Rohit"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && void loadData()}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label style={{ fontSize: 11 }}>Category</label>
                <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                  <option value="">All Categories</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.category_code}>{c.category_name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label style={{ fontSize: 11 }}>Status</label>
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                  <option value="">All Statuses</option>
                  <option value="in_stock">In Stock / Available</option>
                  <option value="in_use">In Use / Assigned</option>
                  <option value="assigned">Assigned (Pending Ack)</option>
                  <option value="under_maintenance">Under Maintenance</option>
                  <option value="damaged">Damaged</option>
                  <option value="lost">Lost / Stolen</option>
                  <option value="disposed">Disposed / Retired</option>
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label style={{ fontSize: 11 }}>Department</label>
                <select value={filterDept} onChange={(e) => setFilterDept(e.target.value)}>
                  <option value="">All Departments</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0, display: 'flex', alignItems: 'flex-end' }}>
                <button
                  className={`btn-quiet ${filterWarrantyExpiring ? 'active' : ''}`}
                  style={{ width: '100%', height: 38 }}
                  onClick={() => setFilterWarrantyExpiring(!filterWarrantyExpiring)}
                >
                  <Icon name="alertTriangle" size="xs" />
                  <span>Warranty Expiring (60d)</span>
                </button>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="panel">
            <div className="panel-head">
              <strong>Enterprise Asset Master Register</strong>
              <span className="muted" style={{ fontSize: 12 }}>Showing {assets.length} assets</span>
            </div>
            <div className="table-responsive">
              <table>
                <thead>
                  <tr>
                    <th>Asset ID</th>
                    <th>Category</th>
                    <th>Device & Specification</th>
                    <th>Serial / IMEI</th>
                    <th>Current Holder</th>
                    <th>Location / Dept</th>
                    <th>Condition</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {assets.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="empty">
                        No assets matching filter criteria. Click "Register Asset" to add one.
                      </td>
                    </tr>
                  ) : (
                    assets.map((a) => (
                      <tr key={a.id}>
                        <td>
                          <button
                            className="btn-link mono"
                            style={{ fontWeight: 600 }}
                            onClick={() => void handleOpenDetail(a.id)}
                          >
                            {a.asset_code}
                          </button>
                        </td>
                        <td>
                          <span className="badge">{a.category_display_name || a.category}</span>
                        </td>
                        <td>
                          <div><strong>{a.brand}</strong> {a.model || a.asset_name}</div>
                          {a.purchase_cost && (
                            <span className="muted mono" style={{ fontSize: 11 }}>
                              Cost: {formatMoney(a.purchase_cost)}
                            </span>
                          )}
                        </td>
                        <td className="mono muted" style={{ fontSize: 12 }}>
                          {a.serial_number || a.imei || '—'}
                        </td>
                        <td>
                          {a.holder_first_name ? (
                            <div>
                              <strong>{a.holder_first_name} {a.holder_last_name}</strong>
                              <div className="mono muted" style={{ fontSize: 11 }}>{a.holder_employee_code}</div>
                            </div>
                          ) : (
                            <span className="muted">— Unassigned —</span>
                          )}
                        </td>
                        <td>
                          <div>{a.location || 'Headquarters'}</div>
                          {a.department_name && <div className="muted" style={{ fontSize: 11 }}>{a.department_name}</div>}
                        </td>
                        <td>
                          <span className={`pill ${a.condition === 'new' ? 'success' : a.condition === 'damaged' ? 'danger' : 'neutral'}`}>
                            {a.condition}
                          </span>
                        </td>
                        <td>
                          <StatusBadge status={a.status} />
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <div style={{ display: 'inline-flex', gap: 6 }}>
                            <button
                              className="btn-icon btn-sm"
                              title="View Details & Lifecycle History"
                              onClick={() => void handleOpenDetail(a.id)}
                            >
                              <Icon name="eye" size="xs" />
                            </button>

                            {/* Allocate if available */}
                            {(a.status === 'in_stock' || a.status === 'available') && (
                              <button
                                className="btn-quiet btn-sm"
                                title="Allocate to Employee"
                                onClick={() => {
                                  setAllocModal(a);
                                  if (employees.length) setSelectedEmpId(employees[0].id);
                                }}
                              >
                                <Icon name="userCheck" size="xs" />
                                <span>Allocate</span>
                              </button>
                            )}

                            {/* Transfer if in use */}
                            {(a.status === 'in_use' || a.status === 'assigned' || a.status === 'allocated') && (
                              <button
                                className="btn-quiet btn-sm"
                                title="Transfer Asset"
                                onClick={() => {
                                  setTransferModal(a);
                                  if (employees.length) setToEmpId(employees[0].id);
                                }}
                              >
                                <Icon name="arrowRight" size="xs" />
                                <span>Transfer</span>
                              </button>
                            )}

                            {/* Service / Maintenance */}
                            {a.status !== 'disposed' && a.status !== 'under_maintenance' && (
                              <button
                                className="btn-quiet btn-sm"
                                title="Log Maintenance / Repair"
                                onClick={() => setMaintenanceModal(a)}
                              >
                                <Icon name="refresh" size="xs" />
                              </button>
                            )}

                            {/* Log Incident */}
                            {a.status !== 'disposed' && (
                              <button
                                className="btn-quiet btn-sm"
                                title="Report Damage or Loss"
                                onClick={() => setIncidentModal(a)}
                              >
                                <Icon name="alertTriangle" size="xs" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : tab === 'myassets' ? (
        /* MY COMPANY ASSETS TAB (Employee Profile) */
        <div>
          <div className="panel">
            <div className="panel-head">
              <div>
                <strong>My Company Assigned Devices & Accessories</strong>
                <div className="muted" style={{ fontSize: 12 }}>
                  Official hardware and tools issued to your profile: {session?.user.fullName} ({session?.user.email}). Please report any issues promptly.
                </div>
              </div>
            </div>

            {/* Pending Acknowledgement Alert */}
            {myAssets.some((ma) => ma.acknowledgement_status === 'pending') && (
              <div className="banner warning" style={{ margin: 16 }}>
                <div>
                  <strong>Action Required: Digital Handover Acknowledgement</strong>
                  <div>You have unacknowledged company assets. Please review the item conditions and click "Acknowledge" below.</div>
                </div>
              </div>
            )}

            <div className="table-responsive">
              <table>
                <thead>
                  <tr>
                    <th>Asset ID</th>
                    <th>Category</th>
                    <th>Device Details</th>
                    <th>Serial / IMEI</th>
                    <th>Allocated Date</th>
                    <th>Condition</th>
                    <th>Included Accessories</th>
                    <th>Acknowledgement</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {myAssets.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="empty">
                        No company devices currently assigned to your account.
                      </td>
                    </tr>
                  ) : (
                    myAssets.map((ma) => (
                      <tr key={ma.allocation_id}>
                        <td className="mono" style={{ fontWeight: 600 }}>{ma.asset_code}</td>
                        <td>
                          <span className="badge">{ma.category_display_name || ma.category}</span>
                        </td>
                        <td>
                          <strong>{ma.brand}</strong> {ma.model || ma.asset_name}
                        </td>
                        <td className="mono muted" style={{ fontSize: 12 }}>{ma.serial_number || ma.imei || '—'}</td>
                        <td className="mono">{ma.allocated_at?.split('T')[0]}</td>
                        <td>
                          <span className="pill neutral">{ma.condition}</span>
                        </td>
                        <td>
                          {ma.accessories && ma.accessories.length > 0 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                              {ma.accessories.map((acc) => (
                                <span key={acc.id} className="badge" style={{ fontSize: 11 }}>
                                  {acc.name}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="muted" style={{ fontSize: 12 }}>Standard package</span>
                          )}
                        </td>
                        <td>
                          {ma.acknowledgement_status === 'acknowledged' ? (
                            <span className="badge success">
                              <Icon name="check" size="xs" />
                              <span>Acknowledged</span>
                            </span>
                          ) : (
                            <span className="badge warning">Pending Ack</span>
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            {ma.acknowledgement_status === 'pending' && (
                              <button
                                className="btn-primary btn-sm"
                                onClick={() => {
                                  setAckModal(ma);
                                  setAckTermsAccepted(false);
                                }}
                              >
                                <Icon name="shieldCheck" size="xs" />
                                <span>Acknowledge</span>
                              </button>
                            )}
                            <button
                              className="btn-quiet btn-sm"
                              onClick={() => {
                                setReturnModal({
                                  allocId: ma.allocation_id,
                                  assetCode: ma.asset_code,
                                  defaultCondition: ma.condition,
                                });
                              }}
                            >
                              <Icon name="refresh" size="xs" />
                              <span>Return</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : tab === 'reports' ? (
        /* REPORTS & EXPORTS TAB */
        <div>
          <div className="panel" style={{ marginBottom: 16 }}>
            <div className="panel-head">
              <strong>Enterprise Asset Intelligence & Reports</strong>
              <button className="btn-primary btn-sm" onClick={handleExportCSV} disabled={reportRows.length === 0}>
                <Icon name="download" size="xs" />
                <span>Export to CSV</span>
              </button>
            </div>
            <div style={{ padding: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                className={`btn-quiet ${selectedReportType === 'asset_register' ? 'active' : ''}`}
                onClick={() => void handleFetchReport('asset_register')}
              >
                Asset Register
              </button>
              <button
                className={`btn-quiet ${selectedReportType === 'employee_register' ? 'active' : ''}`}
                onClick={() => void handleFetchReport('employee_register')}
              >
                Employee Assignments
              </button>
              <button
                className={`btn-quiet ${selectedReportType === 'cost_and_depreciation' ? 'active' : ''}`}
                onClick={() => void handleFetchReport('cost_and_depreciation')}
              >
                Cost & Depreciation
              </button>
              <button
                className={`btn-quiet ${selectedReportType === 'maintenance_history' ? 'active' : ''}`}
                onClick={() => void handleFetchReport('maintenance_history')}
              >
                Maintenance Log
              </button>
              <button
                className={`btn-quiet ${selectedReportType === 'incidents_and_recovery' ? 'active' : ''}`}
                onClick={() => void handleFetchReport('incidents_and_recovery')}
              >
                Damage / Loss Recovery
              </button>
            </div>
          </div>

          <div className="panel">
            <div className="panel-head">
              <strong>Report Preview ({reportRows.length} rows)</strong>
            </div>
            {loadingReport ? (
              <div className="empty">Generating report data...</div>
            ) : reportRows.length === 0 ? (
              <div className="empty">No records found for this report filter.</div>
            ) : (
              <div className="table-responsive">
                <table>
                  <thead>
                    <tr>
                      {Object.keys(reportRows[0]).map((h) => (
                        <th key={h} style={{ textTransform: 'capitalize' }}>
                          {h.replace(/_/g, ' ')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {reportRows.map((r, i) => (
                      <tr key={i}>
                        {Object.keys(reportRows[0]).map((h) => (
                          <td key={h} className={typeof r[h] === 'number' ? 'mono' : ''}>
                            {String(r[h] ?? '—')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {/* ========================================================= */}
      {/* MODALS & DRAWERS */}
      {/* ========================================================= */}

      {/* 1. ASSET DETAIL DRAWER */}
      {detailModal && (
        <div className="modal-backdrop" onClick={() => setDetailModal(null)}>
          <div className="modal-content" style={{ maxWidth: 780 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <span className="eyebrow">Enterprise Master Record</span>
                <h3>Asset Overview & Lifecycle History</h3>
              </div>
              <button className="btn-icon" onClick={() => setDetailModal(null)}>
                <Icon name="close" size="sm" />
              </button>
            </div>

            {loadingDetail || !selectedAssetDetail ? (
              <div className="empty">Loading comprehensive asset history...</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {/* Top Summary */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, background: 'rgba(255,255,255,0.03)', padding: 14, borderRadius: 8 }}>
                  <div>
                    <span className="muted" style={{ fontSize: 11 }}>Asset Code</span>
                    <div className="mono" style={{ fontSize: 16, fontWeight: 700 }}>{selectedAssetDetail.asset.asset_code}</div>
                  </div>
                  <div>
                    <span className="muted" style={{ fontSize: 11 }}>Category</span>
                    <div>{selectedAssetDetail.asset.category_display_name || selectedAssetDetail.asset.category}</div>
                  </div>
                  <div>
                    <span className="muted" style={{ fontSize: 11 }}>Current Status</span>
                    <div><StatusBadge status={selectedAssetDetail.asset.status} /></div>
                  </div>
                  <div>
                    <span className="muted" style={{ fontSize: 11 }}>Current Holder</span>
                    <div>
                      {selectedAssetDetail.asset.holder_first_name
                        ? `${selectedAssetDetail.asset.holder_first_name} ${selectedAssetDetail.asset.holder_last_name}`
                        : '— In Stock —'}
                    </div>
                  </div>
                </div>

                {/* Specs & Hardware Attributes */}
                <div>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: 14 }}>Hardware & Identification</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, fontSize: 13 }}>
                    <div><span className="muted">Brand / Maker:</span> <strong>{selectedAssetDetail.asset.brand || '—'}</strong></div>
                    <div><span className="muted">Model:</span> <strong>{selectedAssetDetail.asset.model || '—'}</strong></div>
                    <div><span className="muted">Serial No:</span> <span className="mono">{selectedAssetDetail.asset.serial_number || '—'}</span></div>
                    <div><span className="muted">IMEI:</span> <span className="mono">{selectedAssetDetail.asset.imei || '—'}</span></div>
                    <div><span className="muted">MAC Address:</span> <span className="mono">{selectedAssetDetail.asset.mac_address || '—'}</span></div>
                    <div><span className="muted">Location:</span> <span>{selectedAssetDetail.asset.location || 'Headquarters'}</span></div>
                  </div>
                </div>

                {/* Financial & Warranty Information */}
                <div>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: 14 }}>Procurement & Warranty</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, fontSize: 13 }}>
                    <div><span className="muted">Purchase Date:</span> <span>{selectedAssetDetail.asset.purchase_date || '—'}</span></div>
                    <div><span className="muted">Acquisition Cost:</span> <strong>{formatMoney(selectedAssetDetail.asset.purchase_cost || 0)}</strong></div>
                    <div><span className="muted">Book Value:</span> <strong>{formatMoney(selectedAssetDetail.asset.current_book_value || selectedAssetDetail.asset.purchase_cost || 0)}</strong></div>
                    <div><span className="muted">Vendor:</span> <span>{selectedAssetDetail.asset.vendor_name || '—'}</span></div>
                    <div><span className="muted">Warranty Expiry:</span> <span className="mono">{selectedAssetDetail.asset.warranty_end || '—'}</span></div>
                    <div><span className="muted">Cost Center:</span> <span>{selectedAssetDetail.asset.cost_center || 'General'}</span></div>
                  </div>
                </div>

                {/* Linked Accessories */}
                <div>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: 14 }}>Linked Accessories & Peripherals</h4>
                  {selectedAssetDetail.accessories.length === 0 ? (
                    <div className="muted" style={{ fontSize: 12 }}>No separate accessories linked to this asset.</div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {selectedAssetDetail.accessories.map((acc) => (
                        <div key={acc.id} className="badge" style={{ padding: '6px 10px' }}>
                          <strong>{acc.name}</strong> ({acc.accessory_type}) — {acc.condition}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Allocation & Movement History Timeline */}
                <div>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: 14 }}>Assignment & Transfer History</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 200, overflowY: 'auto' }}>
                    {selectedAssetDetail.allocationHistory.map((alloc) => (
                      <div key={alloc.id} style={{ padding: 10, background: 'rgba(255,255,255,0.02)', borderRadius: 6, fontSize: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <strong>{alloc.employee_first_name} {alloc.employee_last_name} ({alloc.employee_code})</strong>
                          <span className="mono muted">{alloc.allocated_at?.split('T')[0]}</span>
                        </div>
                        <div className="muted">
                          Status: {alloc.status} | Condition: {alloc.issued_condition || 'good'} | Ack: {alloc.acknowledgement_status || 'pending'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
                  <button
                    className="btn-quiet"
                    style={{ color: '#ef4444' }}
                    onClick={() => {
                      setDisposeModal(selectedAssetDetail.asset);
                      setDetailModal(null);
                    }}
                  >
                    <Icon name="trash" size="xs" />
                    <span>Dispose / Retire Asset</span>
                  </button>
                  <button className="btn-quiet" onClick={() => setDetailModal(null)}>Close</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 2. CREATE ASSET MODAL */}
      {createModal && (
        <div className="modal-backdrop" onClick={() => setCreateModal(false)}>
          <div className="modal-content" style={{ maxWidth: 680 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <span className="eyebrow">Procurement & Master Entry</span>
                <h3>Register New Enterprise Asset</h3>
              </div>
              <button className="btn-icon" onClick={() => setCreateModal(false)}>
                <Icon name="close" size="sm" />
              </button>
            </div>
            <form onSubmit={handleCreateAsset}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-grid-2">
                  <div className="form-group">
                    <label>Asset Code (leave blank for auto-generation)</label>
                    <input type="text" placeholder="e.g. AST-LAP-2026-0001" value={code} onChange={(e) => setCode(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Category</label>
                    <select
                      value={category}
                      onChange={(e) => {
                        setCategory(e.target.value);
                        const sel = categories.find((c) => c.category_code === e.target.value);
                        if (sel) setCategoryId(sel.id);
                      }}
                    >
                      {categories.map((c) => (
                        <option key={c.id} value={c.category_code}>{c.category_name}</option>
                      ))}
                      <option value="Laptop">Laptop</option>
                      <option value="Desktop">Desktop / Workstation</option>
                      <option value="Monitor">Monitor</option>
                      <option value="Mobile">Mobile Device</option>
                    </select>
                  </div>
                </div>

                <div className="form-grid-2">
                  <div className="form-group">
                    <label>Brand / Manufacturer</label>
                    <input type="text" required value={brand} onChange={(e) => setBrand(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Model & Specifications</label>
                    <input type="text" required value={model} onChange={(e) => setModel(e.target.value)} />
                  </div>
                </div>

                <div className="form-grid-3">
                  <div className="form-group">
                    <label>Serial Number</label>
                    <input type="text" placeholder="Hardware S/N" value={serial} onChange={(e) => setSerial(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>IMEI (if cellular)</label>
                    <input type="text" placeholder="15-digit IMEI" value={imei} onChange={(e) => setImei(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>MAC Address</label>
                    <input type="text" placeholder="00:1A:2B:3C:4D:5E" value={macAddress} onChange={(e) => setMacAddress(e.target.value)} />
                  </div>
                </div>

                <div className="form-grid-3">
                  <div className="form-group">
                    <label>Purchase Date</label>
                    <input type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Acquisition Cost (₹)</label>
                    <input type="number" min="0" value={cost} onChange={(e) => setCost(Number(e.target.value))} />
                  </div>
                  <div className="form-group">
                    <label>Warranty (Months)</label>
                    <input type="number" min="0" value={warrantyMonths} onChange={(e) => setWarrantyMonths(Number(e.target.value))} />
                  </div>
                </div>

                <div className="form-grid-3">
                  <div className="form-group">
                    <label>Location</label>
                    <input type="text" value={location} onChange={(e) => setLocation(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Department</label>
                    <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
                      <option value="">— Unallocated / Pool —</option>
                      {departments.map((d) => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Initial Condition</label>
                    <select value={condition} onChange={(e) => setCondition(e.target.value)}>
                      <option value="new">Brand New</option>
                      <option value="good">Good / Functional</option>
                      <option value="fair">Fair</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Initial Included Accessories (comma-separated)</label>
                  <input type="text" value={accessoriesInput} onChange={(e) => setAccessoriesInput(e.target.value)} />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <button type="button" className="btn-quiet" onClick={() => setCreateModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Register into Inventory</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 3. ALLOCATE ASSET MODAL */}
      {allocModal && (
        <div className="modal-backdrop" onClick={() => setAllocModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <span className="eyebrow">Handover & Custody</span>
                <h3>Allocate Asset: {allocModal.asset_code}</h3>
              </div>
              <button className="btn-icon" onClick={() => setAllocModal(null)}>
                <Icon name="close" size="sm" />
              </button>
            </div>
            <form onSubmit={handleAllocateSubmit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-group">
                  <label>Assign to Employee</label>
                  <select value={selectedEmpId} onChange={(e) => setSelectedEmpId(e.target.value)} required>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name} ({emp.employee_code}) - {emp.designation || 'Staff'}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-grid-2">
                  <div className="form-group">
                    <label>Allocation Type</label>
                    <select value={allocType} onChange={(e) => setAllocType(e.target.value as any)}>
                      <option value="permanent">Permanent / Employment</option>
                      <option value="temporary">Temporary / Project Loan</option>
                      <option value="project_based">Project Specific</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Expected Return Date (if temporary)</label>
                    <input type="date" value={expectedReturnDate} onChange={(e) => setExpectedReturnDate(e.target.value)} />
                  </div>
                </div>

                <div className="form-group">
                  <label>Condition at Handover</label>
                  <select value={allocCondition} onChange={(e) => setAllocCondition(e.target.value)}>
                    <option value="new">Brand New</option>
                    <option value="good">Good / Working</option>
                    <option value="fair">Fair / Functional with minor wear</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Accessories Handed Over (comma-separated)</label>
                  <input
                    type="text"
                    placeholder="e.g. Charger, Laptop Bag, Security Dongle"
                    value={allocAccessories}
                    onChange={(e) => setAllocAccessories(e.target.value)}
                  />
                </div>

                <div className="banner info">
                  Allocating this asset records the assignment timestamp and notifies the employee to complete digital acknowledgement.
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <button type="button" className="btn-quiet" onClick={() => setAllocModal(null)}>Cancel</button>
                <button type="submit" className="btn-primary">Confirm Handover</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 4. DIGITAL ACKNOWLEDGEMENT MODAL */}
      {ackModal && (
        <div className="modal-backdrop" onClick={() => setAckModal(null)}>
          <div className="modal-content" style={{ maxWidth: 600 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <span className="eyebrow">Digital Handover Verification</span>
                <h3>Employee Asset Custody Acknowledgement</h3>
              </div>
              <button className="btn-icon" onClick={() => setAckModal(null)}>
                <Icon name="close" size="sm" />
              </button>
            </div>
            <form onSubmit={handleAcknowledgeSubmit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ background: 'rgba(255,255,255,0.03)', padding: 14, borderRadius: 8, fontSize: 13 }}>
                  <div><strong>Device:</strong> {ackModal.brand} {ackModal.model} ({ackModal.asset_code})</div>
                  <div><strong>Serial:</strong> {ackModal.serial_number || '—'}</div>
                  <div><strong>Condition at Issue:</strong> {ackModal.issued_condition || 'Good'}</div>
                </div>

                <div style={{ border: '1px solid var(--border)', padding: 14, borderRadius: 8, fontSize: 12, lineHeight: 1.6 }}>
                  <strong>Terms of Custody & Responsibility:</strong>
                  <p style={{ margin: '8px 0 0 0' }}>
                    I acknowledge receipt of this company asset in the condition described. I undertake to exercise due care, safeguard it against loss, theft or negligence, comply with corporate IT security guidelines, and promptly surrender the device and associated peripherals upon company request or separation.
                  </p>
                </div>

                <div className="form-group">
                  <label>Remarks or Discrepancies (optional)</label>
                  <input
                    type="text"
                    placeholder="e.g. Received with minor surface scratch on lid"
                    value={ackComments}
                    onChange={(e) => setAckComments(e.target.value)}
                  />
                </div>

                <label style={{ display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer', fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={ackTermsAccepted}
                    onChange={(e) => setAckTermsAccepted(e.target.checked)}
                    required
                  />
                  <span>I verify and accept custody of this company asset under the stated terms.</span>
                </label>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <button type="button" className="btn-quiet" onClick={() => setAckModal(null)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={!ackTermsAccepted}>
                  Accept & Acknowledge
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 5. TRANSFER ASSET MODAL */}
      {transferModal && (
        <div className="modal-backdrop" onClick={() => setTransferModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <span className="eyebrow">Chain of Custody Transfer</span>
                <h3>Transfer Asset: {transferModal.asset_code}</h3>
              </div>
              <button className="btn-icon" onClick={() => setTransferModal(null)}>
                <Icon name="close" size="sm" />
              </button>
            </div>
            <form onSubmit={handleTransferSubmit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-group">
                  <label>Transfer Type</label>
                  <select value={transferType} onChange={(e) => setTransferType(e.target.value as any)}>
                    <option value="employee_to_employee">Employee to Employee</option>
                    <option value="department_to_department">Department to Department</option>
                    <option value="location_to_location">Location to Location</option>
                  </select>
                </div>

                {transferType === 'employee_to_employee' && (
                  <div className="form-group">
                    <label>Transfer to Employee</label>
                    <select value={toEmpId} onChange={(e) => setToEmpId(e.target.value)} required>
                      {employees.map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.first_name} {emp.last_name} ({emp.employee_code})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {transferType === 'department_to_department' && (
                  <div className="form-group">
                    <label>Target Department</label>
                    <select value={toDeptId} onChange={(e) => setToDeptId(e.target.value)} required>
                      {departments.map((d) => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {transferType === 'location_to_location' && (
                  <div className="form-group">
                    <label>Target Location</label>
                    <input type="text" required placeholder="e.g. Bangalore Branch" value={toLoc} onChange={(e) => setToLoc(e.target.value)} />
                  </div>
                )}

                <div className="form-group">
                  <label>Condition at Transfer</label>
                  <select value={transferCondition} onChange={(e) => setTransferCondition(e.target.value)}>
                    <option value="good">Good / Functional</option>
                    <option value="fair">Fair</option>
                    <option value="damaged">Damaged</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Transfer Reason</label>
                  <input type="text" required placeholder="e.g. Team realignment / Project handover" value={transferReason} onChange={(e) => setTransferReason(e.target.value)} />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <button type="button" className="btn-quiet" onClick={() => setTransferModal(null)}>Cancel</button>
                <button type="submit" className="btn-primary">Execute Transfer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 6. PHYSICAL RETURN & INSPECTION MODAL */}
      {returnModal && (
        <div className="modal-backdrop" onClick={() => setReturnModal(null)}>
          <div className="modal-content" style={{ maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <span className="eyebrow">Physical Return & Inspection</span>
                <h3>Check-in Asset: {returnModal.assetCode}</h3>
              </div>
              <button className="btn-icon" onClick={() => setReturnModal(null)}>
                <Icon name="close" size="sm" />
              </button>
            </div>
            <form onSubmit={handleReturnInspectionSubmit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-grid-2">
                  <div className="form-group">
                    <label>Physical Condition on Return</label>
                    <select value={inspectCondition} onChange={(e) => setInspectCondition(e.target.value)}>
                      <option value="good">Good / Undamaged</option>
                      <option value="fair">Fair / Normal Wear</option>
                      <option value="damaged">Damaged (Requires Repair)</option>
                      <option value="decommissioned">Defective / Scrap</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Inspection Verdict</label>
                    <select value={inspectResult} onChange={(e) => setInspectResult(e.target.value as any)}>
                      <option value="passed">Passed — Ready for Restock</option>
                      <option value="minor_wear">Minor Wear — Accepted</option>
                      <option value="repair_required">Repair Required — Route to Service</option>
                      <option value="total_loss">Total Loss / Severe Damage</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={inspectHasDamage}
                      onChange={(e) => setInspectHasDamage(e.target.checked)}
                    />
                    <span>Has Visible Physical Damage</span>
                  </label>
                </div>

                {inspectHasDamage && (
                  <div className="form-group">
                    <label>Damage Description</label>
                    <input
                      type="text"
                      placeholder="e.g. Cracked screen, non-functioning keyboard"
                      value={inspectDamageDesc}
                      onChange={(e) => setInspectDamageDesc(e.target.value)}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Returned Accessories (comma-separated)</label>
                  <input
                    type="text"
                    placeholder="e.g. Power Cable, Laptop Bag"
                    value={inspectAccessoriesReturned}
                    onChange={(e) => setInspectAccessoriesReturned(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Missing Accessories (if any)</label>
                  <input
                    type="text"
                    placeholder="e.g. HDMI Adapter"
                    value={inspectMissingAccessories}
                    onChange={(e) => setInspectMissingAccessories(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Inspector Remarks</label>
                  <input
                    type="text"
                    placeholder="Physical hardware inspection verified by IT support"
                    value={inspectorRemarks}
                    onChange={(e) => setInspectorRemarks(e.target.value)}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <button type="button" className="btn-quiet" onClick={() => setReturnModal(null)}>Cancel</button>
                <button type="submit" className="btn-primary">Complete Inspection & Return</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 7. MAINTENANCE LOG MODAL */}
      {maintenanceModal && (
        <div className="modal-backdrop" onClick={() => setMaintenanceModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <span className="eyebrow">Service & Repair Request</span>
                <h3>Maintenance for {maintenanceModal.asset_code}</h3>
              </div>
              <button className="btn-icon" onClick={() => setMaintenanceModal(null)}>
                <Icon name="close" size="sm" />
              </button>
            </div>
            <form onSubmit={handleCreateMaintenanceSubmit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-grid-2">
                  <div className="form-group">
                    <label>Service Type</label>
                    <select value={maintServiceType} onChange={(e) => setMaintServiceType(e.target.value as any)}>
                      <option value="repair">Hardware Repair</option>
                      <option value="preventive">Preventive Maintenance</option>
                      <option value="warranty_claim">OEM Warranty Claim</option>
                      <option value="upgrade">Hardware Upgrade (RAM/SSD)</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Start Date</label>
                    <input type="date" value={maintStartDate} onChange={(e) => setMaintStartDate(e.target.value)} />
                  </div>
                </div>

                <div className="form-group">
                  <label>Issue Description / Scope of Work</label>
                  <textarea
                    rows={3}
                    required
                    placeholder="Describe failure, screen issue, battery degradation, etc."
                    value={maintIssue}
                    onChange={(e) => setMaintIssue(e.target.value)}
                  />
                </div>

                <div className="form-grid-2">
                  <div className="form-group">
                    <label>Estimated Cost (₹)</label>
                    <input type="number" min="0" value={maintEstCost} onChange={(e) => setMaintEstCost(Number(e.target.value))} />
                  </div>
                  <div className="form-group" style={{ display: 'flex', alignItems: 'center', marginTop: 22 }}>
                    <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
                      <input type="checkbox" checked={maintIsWarranty} onChange={(e) => setMaintIsWarranty(e.target.checked)} />
                      <span>Covered by OEM Warranty</span>
                    </label>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <button type="button" className="btn-quiet" onClick={() => setMaintenanceModal(null)}>Cancel</button>
                <button type="submit" className="btn-primary">Initiate Service</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 8. COMPLETE MAINTENANCE MODAL */}
      {completeMaintModal && (
        <div className="modal-backdrop" onClick={() => setCompleteMaintModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <span className="eyebrow">Service Completion</span>
                <h3>Resolve Maintenance: {completeMaintModal.code}</h3>
              </div>
              <button className="btn-icon" onClick={() => setCompleteMaintModal(null)}>
                <Icon name="close" size="sm" />
              </button>
            </div>
            <form onSubmit={handleCompleteMaintenanceSubmit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-grid-2">
                  <div className="form-group">
                    <label>Completion Date</label>
                    <input type="date" value={compMaintDate} onChange={(e) => setCompMaintDate(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Downtime (Days)</label>
                    <input type="number" min="0" value={compMaintDowntime} onChange={(e) => setCompMaintDowntime(Number(e.target.value))} />
                  </div>
                </div>

                <div className="form-group">
                  <label>Actual Repair Cost (₹)</label>
                  <input type="number" min="0" value={compMaintActualCost} onChange={(e) => setCompMaintActualCost(Number(e.target.value))} />
                </div>

                <div className="form-group">
                  <label>Resolution Notes</label>
                  <textarea
                    rows={3}
                    value={compMaintNotes}
                    onChange={(e) => setCompMaintNotes(e.target.value)}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <button type="button" className="btn-quiet" onClick={() => setCompleteMaintModal(null)}>Cancel</button>
                <button type="submit" className="btn-primary">Mark as Completed</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 9. INCIDENT DAMAGE / LOSS MODAL */}
      {incidentModal && (
        <div className="modal-backdrop" onClick={() => setIncidentModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <span className="eyebrow">Damage & Loss Incident Assessment</span>
                <h3>Report Incident: {incidentModal.asset_code}</h3>
              </div>
              <button className="btn-icon" onClick={() => setIncidentModal(null)}>
                <Icon name="close" size="sm" />
              </button>
            </div>
            <form onSubmit={handleCreateIncidentSubmit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-grid-2">
                  <div className="form-group">
                    <label>Incident Type</label>
                    <select value={incidentType} onChange={(e) => setIncidentType(e.target.value as any)}>
                      <option value="damaged">Hardware Damage</option>
                      <option value="lost">Lost / Misplaced</option>
                      <option value="stolen">Stolen (FIR / Police Report)</option>
                      <option value="missing_accessory">Missing Essential Accessory</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Incident Date</label>
                    <input type="date" value={incidentDate} onChange={(e) => setIncidentDate(e.target.value)} />
                  </div>
                </div>

                <div className="form-group">
                  <label>Incident Narrative & Circumstances</label>
                  <textarea
                    rows={3}
                    required
                    placeholder="Describe how and where the incident occurred..."
                    value={incidentDesc}
                    onChange={(e) => setIncidentDesc(e.target.value)}
                  />
                </div>

                <div className="form-grid-2">
                  <div className="form-group">
                    <label>Estimated Repair / Replacement Cost (₹)</label>
                    <input type="number" min="0" value={incidentEstCost} onChange={(e) => setIncidentEstCost(Number(e.target.value))} />
                  </div>
                  <div className="form-group">
                    <label>Proposed Responsibility (%)</label>
                    <input type="number" min="0" max="100" value={incidentEmpRespPct} onChange={(e) => setIncidentEmpRespPct(Number(e.target.value))} />
                  </div>
                </div>

                <div className="banner info">
                  Assessment will be logged and submitted for HR/Finance approval. No salary deduction is processed automatically without authorized recovery instruction.
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <button type="button" className="btn-quiet" onClick={() => setIncidentModal(null)}>Cancel</button>
                <button type="submit" className="btn-primary">Submit Incident Report</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 10. DISPOSE ASSET MODAL */}
      {disposeModal && (
        <div className="modal-backdrop" onClick={() => setDisposeModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <span className="eyebrow">Decommission & Disposal</span>
                <h3>Retire Asset: {disposeModal.asset_code}</h3>
              </div>
              <button className="btn-icon" onClick={() => setDisposeModal(null)}>
                <Icon name="close" size="sm" />
              </button>
            </div>
            <form onSubmit={handleDisposeSubmit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-grid-2">
                  <div className="form-group">
                    <label>Disposal Date</label>
                    <input type="date" value={disposeDate} onChange={(e) => setDisposeDate(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Disposal / Scrap Proceeds (₹)</label>
                    <input type="number" min="0" value={disposeProceeds} onChange={(e) => setDisposeProceeds(Number(e.target.value))} />
                  </div>
                </div>

                <div className="form-group">
                  <label>Disposal Reason / Method</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Scrapped, Recycled under E-Waste norms, Sold"
                    value={disposeReason}
                    onChange={(e) => setDisposeReason(e.target.value)}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <button type="button" className="btn-quiet" onClick={() => setDisposeModal(null)}>Cancel</button>
                <button type="submit" className="btn-primary" style={{ background: '#ef4444' }}>Confirm Decommission</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 11. CATEGORY CREATION MODAL */}
      {categoryModal && (
        <div className="modal-backdrop" onClick={() => setCategoryModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <span className="eyebrow">Master Configuration</span>
                <h3>Define Asset Category</h3>
              </div>
              <button className="btn-icon" onClick={() => setCategoryModal(false)}>
                <Icon name="close" size="sm" />
              </button>
            </div>
            <form onSubmit={handleCreateCategorySubmit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-grid-2">
                  <div className="form-group">
                    <label>Category Code</label>
                    <input type="text" required placeholder="e.g. SERVER" value={catCode} onChange={(e) => setCatCode(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Display Name</label>
                    <input type="text" required placeholder="e.g. Enterprise Servers" value={catName} onChange={(e) => setCatName(e.target.value)} />
                  </div>
                </div>

                <div className="form-grid-2">
                  <div className="form-group">
                    <label>Group Classification</label>
                    <select value={catGroup} onChange={(e) => setCatGroup(e.target.value as any)}>
                      <option value="it_assets">IT Assets</option>
                      <option value="communication">Communication</option>
                      <option value="office">Office & Facilities</option>
                      <option value="travel_ops">Travel / Operations</option>
                      <option value="software_digital">Software / Digital</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>ID Prefix</label>
                    <input type="text" required placeholder="e.g. AST-SRV" value={catPrefix} onChange={(e) => setCatPrefix(e.target.value)} />
                  </div>
                </div>

                <div className="form-grid-2">
                  <div className="form-group">
                    <label>Useful Life (Months)</label>
                    <input type="number" min="1" value={catLife} onChange={(e) => setCatLife(Number(e.target.value))} />
                  </div>
                  <div className="form-group">
                    <label>Depreciation Rate (%/yr)</label>
                    <input type="number" min="0" step="0.1" value={catRate} onChange={(e) => setCatRate(Number(e.target.value))} />
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <button type="button" className="btn-quiet" onClick={() => setCategoryModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Save Category</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
